import { useState, useEffect, useMemo } from "react"
import { Bell, Plus } from "lucide-react"
import { SchoolLayout } from "../components/ui/school/SchoolLayout"
import { ResourcesFilters } from "../components/ui/school/school-resources/resources-filters"
import { ResourceStatsCards } from "../components/ui/school/school-resources/resource-stats-cards"
import { AddResourceModal } from "../components/ui/school/school-resources/add-resource-modal"
import { ResourcesTable } from "../components/ui/school/school-resources/resources-table"
import { PaginationStats } from "../components/ui/school/students-management/pagination-stats"
import { listResources, createResource, deleteResource } from "../hooks/api/schoolApi"

const SchoolResourcesPage = () => {
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({ searchQuery: "", resourceType: "", targetClass: "" })
  const [currentPage, setCurrentPage] = useState(1)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const resultsPerPage = 10

  const load = () => {
    setLoading(true)
    listResources()
      .then((data) =>
        setResources(
          data.map((r) => ({
            id: r._id,
            title: r.title,
            type: r.type?.charAt(0).toUpperCase() + r.type?.slice(1) ?? "PDF",
            targetClass: r.classLevels?.[0] ?? "All",
            uploadedBy: "Admin",
            uploadDate: r.createdAt ? new Date(r.createdAt).toISOString().split("T")[0] : "—",
            downloads: r.downloads ?? 0,
            size: r.fileSize ? `${(r.fileSize / 1024 / 1024).toFixed(1)} MB` : "—",
            fileUrl: r.fileUrl,
            subject: r.subject,
          }))
        )
      )
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const filteredResources = useMemo(() => {
    return resources.filter((r) => {
      const matchesSearch = !filters.searchQuery || r.title.toLowerCase().includes(filters.searchQuery.toLowerCase())
      const matchesType = !filters.resourceType || r.type.toLowerCase() === filters.resourceType.toLowerCase()
      const matchesClass = !filters.targetClass || r.targetClass === filters.targetClass
      return matchesSearch && matchesType && matchesClass
    })
  }, [filters, resources])

  const totalPages = Math.ceil(filteredResources.length / resultsPerPage)
  const paginatedResources = filteredResources.slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage)

  const stats = {
    totalResources: resources.length,
    totalDownloads: resources.reduce((sum, r) => sum + (r.downloads ?? 0), 0),
    activeContributors: new Set(resources.map((r) => r.uploadedBy)).size,
    resourcesThisMonth: resources.filter((r) => {
      if (!r.uploadDate) return false
      const d = new Date(r.uploadDate)
      const now = new Date()
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
    }).length,
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setCurrentPage(1)
  }

  const handleAddResource = async (newResource) => {
    await createResource({
      title: newResource.title,
      type: newResource.type?.toLowerCase() ?? "pdf",
      fileUrl: newResource.fileUrl ?? "#",
      subject: newResource.subject,
      classLevels: newResource.targetClass ? [newResource.targetClass] : [],
      isPublic: true,
    }).catch(() => {})
    load()
  }

  return (
    <SchoolLayout>
      <div className="p-4 md:p-8 overflow-y-auto">
        <header className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">School Resources</h1>
            <p className="text-gray-500 mt-1">Manage and share educational resources</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative p-3 rounded-full hover:bg-white transition border border-gray-200">
              <Bell className="text-gray-600 w-6 h-6" />
              <span className="absolute top-2 right-2 block h-2 w-2 rounded-full bg-red-500"></span>
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition font-medium"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Add Resource</span>
            </button>
          </div>
        </header>

        <ResourceStatsCards
          totalResources={stats.totalResources}
          totalDownloads={stats.totalDownloads}
          activeContributors={stats.activeContributors}
          resourcesThisMonth={stats.resourcesThisMonth}
        />

        <ResourcesFilters onFilterChange={handleFilterChange} />

        {loading ? (
          <div className="flex justify-center py-12 text-gray-400">Loading resources…</div>
        ) : (
          <ResourcesTable resources={paginatedResources} />
        )}

        {!loading && filteredResources.length > 0 && (
          <div className="mt-6">
            <PaginationStats
              currentPage={currentPage}
              totalPages={totalPages}
              totalResults={filteredResources.length}
              resultsPerPage={resultsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      <AddResourceModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleAddResource} />
    </SchoolLayout>
  )
}

export default SchoolResourcesPage
